//* Libraries imports
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  PlusIcon,
  CalendarIcon,
  ClockIcon,
  Camera,
  ChevronRightIcon,
  ChevronLeftIcon,
} from "lucide-react";
import {
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AttachFile } from "@mui/icons-material";
import { Separator } from "@radix-ui/react-separator";

//* Componentes imports
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { Textarea } from "@components/ui/textarea";
import { DataTable } from "@/components/ui";
import DocumentForm from "../../document/DocumentForm";
import TemplateForm from "../../template/TemplateForm";
import CategoryDocumentForm from "../../categorydocument/CategoryDocumentForm";
import { ApplicationSection } from "./ApplicationSection";
import { TutorialSection } from "./TutorialSection";

//* Types imports
import { IProductPhase, IActivity } from "../types";
import { ApiError } from "@/types/api";
import { PhaseFormOutputDTO } from "@features/phaseForm/types";

export const ActivitySection = ({
  phases,
  activePhaseIndex,
  onActivityUpdate,
  onActivityRemove,
  onAddActivity,
  error,
  availableForms,
  loadDocumentos,
  saveFile,
  documents,
  categories,
  templates,
  techinicals,
  formData,
  setTabSelect,
  tabSelect,
  setAppointments,
}: {
  phases: IProductPhase[];
  activePhaseIndex: number;
  onActivityUpdate: (
    phaseIndex: number,
    activityIndex: number,
    activity: IActivity
  ) => void;
  onActivityRemove: (phaseIndex: number, activityIndex: number) => void;
  onAddActivity: (phaseIndex: number) => void;
  error: ApiError | null;
  availableForms: PhaseFormOutputDTO[];
  saveFile: any;
  documents: any;
  templates: any;
  techinicals: any;
  categories: any;
  formData: any;
}) => {
  const [loadingFileTutorial, setLoadingFileTutorial] = useState(false);
  const [loadingFile, setLoadingFile] = useState(false);
  const activePhase = phases[activePhaseIndex];
  const activities = activePhase.activities || [];
  const [activeActivityIndex, setActiveActivityIndex] = useState<number>(0);

  const [showSelect, setShowSelect] = useState(false);
  const [showSelectTemplate, setShowSelectTemplate] = useState(false);

  const [showModalDocument, setShowModalDocument] = useState(false);
  const [showModalAddDocument, setShowModalAddDocument] = useState(false);

  const [selectTypeModal, setSelectTypeModal] = useState("category");
  const [showModalAddTemplate, setShowModalAddTemplate] = useState(false);

  const [showModalCategory, setShowModalCategory] = useState(false);

  const [showModalTemplate, setShowModalTemplate] = useState(false);

  const [selectedCategoryDocument, setSelectedCategoryDocument] = useState("");
  const [selectedCategoryTemplate, setSelectedCategoryTemplate] = useState("");

  // Se não houver fases selecionadas, não renderiza a seção
  useEffect(() => {
    setActiveActivityIndex(0);
  }, [activePhaseIndex]);

  useSelector(() => {
    if (activities.length > 0 && activities[activeActivityIndex] == undefined) {
      setActiveActivityIndex(0);
    }
  }, [activities]);

  if (
    phases.length === 0 ||
    activePhaseIndex < 0 ||
    activePhaseIndex >= phases.length
  ) {
    return null;
  }

  if (activities.length === 0) {
    return (
      <section className="flex flex-col w-full items-start rounded-xl">
        {/* Header */}
        <div className="flex w-full h-9 items-center justify-between mb-5">
          <h1 className="text-2xl font-bold" id="atividades">
            Atividades (0)
          </h1>

          <div className="flex items-center gap-5">
            <Button
              size="icon"
              className="w-[30px] h-[30px] bg-blue-500 rounded-lg"
              onClick={() => onAddActivity(activePhaseIndex)}
            >
              <PlusIcon className="h-4 w-4 text-white" />
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-center p-10 w-full">
          <p
            className="text-grey-light"
            style={{ textAlign: "center", color: "grey" }}
          >
            Nenhuma atividade cadastrada. Clique no botão + para adicionar uma
            atividade.
          </p>
        </div>
      </section>
    );
  }

  const activeActivity = activities[activeActivityIndex];
  if (activeActivity == undefined) {
    return null;
  }
  console.log(
    "atividade index",
    activeActivity,
    activities,
    activeActivityIndex
  );
  const totalActivities = activities.length;

  const handleAddDocument = async (field: string, document: string) => {
    if (document == "new_document") {
      setShowModalDocument(true);
    } else {
      let document_file = documents.filter(
        (item: any) => item._id == document
      )[0];
      let documents_ = { ...activeActivity.documents };
      documents_[document] = document_file;
      const updatedActivity = { ...activeActivity, ["documents"]: documents_ };

      onActivityUpdate(activePhaseIndex, activeActivityIndex, updatedActivity);
    }
  };

  const handleAddTemplate = async (field: string, template: string) => {
    if (template == "new_document") {
      setShowModalTemplate(true);
    } else {
      let document_file = templates.filter(
        (item: any) => item._id == template
      )[0];
      let templates_ = { ...activeActivity.templates };
      templates_[template] = document_file;
      const updatedActivity = { ...activeActivity, ["templates"]: templates_ };

      onActivityUpdate(activePhaseIndex, activeActivityIndex, updatedActivity);
    }
  };

  // Função para atualizar os campos da atividade ativa
  const handleFieldChange = async (field: string, value: any) => {
    if (field == "image") {
      setLoadingFile(true);
      let response = await saveFile(value);
      const updatedActivity = {
        ...activeActivity,
        [field]: response?.data?.path,
      };
      setLoadingFile(false);
      onActivityUpdate(activePhaseIndex, activeActivityIndex, updatedActivity);
    } else {
      const updatedActivity = { ...activeActivity, [field]: value };
      onActivityUpdate(activePhaseIndex, activeActivityIndex, updatedActivity);
    }
  };

  return (
    <section className="flex flex-col w-full items-start rounded-xl">
      {/* Header */}
      <div className="flex w-full h-9 items-center justify-between mb-5">
        <h1 className="text-2xl font-bold" id="atividades">
          Atividades ({totalActivities})
        </h1>

        <div className="flex items-center gap-5">
          <Button
            variant="outline"
            size="icon"
            disabled={loadingFile || loadingFileTutorial}
            className="w-[30px] h-[30px] bg-clarofundo-01 border-[#d8e0f0] rounded-lg"
            onClick={() =>
              onActivityRemove(activePhaseIndex, activeActivityIndex)
            }
          >
            <img src="/small/excluir.svg" className="h-4 w-4" />
          </Button>

          <Button
            size="icon"
            className="w-[30px] h-[30px] bg-blue-500 rounded-lg"
            onClick={() => onAddActivity(activePhaseIndex)}
          >
            <PlusIcon className="h-4 w-4 text-white" />
          </Button>
        </div>
      </div>

      {error?.validationErrors?.[`activities`] && (
        <p className="text-red-500 text-sm px-1.5 mb-3">
          {error.validationErrors[`activities`].message}
        </p>
      )}

      <Separator className="my-1" />

      {/* Activity Steps */}
      <div className="flex w-full items-center justify-start mb-5">
        <div className="flex items-center gap-2.5 grow">
          {activities.map((activity, index) => (
            <Button
              disabled={loadingFile || loadingFileTutorial}
              key={activity._id || `activity-${index}`}
              variant={index === activeActivityIndex ? "default" : "outline"}
              className={`w-[30px] h-[25px] px-1.5 py-1 rounded ${
                index === activeActivityIndex
                  ? "bg-blue-500 text-white"
                  : "bg-clarofrente-01 text-clarotextos-01 border-[0.5px] border-[#d8e0f0]"
              }`}
              onClick={() => setActiveActivityIndex(index)}
            >
              {String(index + 1).padStart(2, "0")}
            </Button>
          ))}
        </div>
      </div>

      <Separator className="mb-5" />

      {/* Activity Preview */}
      <div className="flex w-full items-start mb-5">
        <div className="flex items-center gap-[18px] w-full">
          <div style={{ position: "relative", display: "flex" }}>
            <input
              type="file"
              accept="image/*"
              id={"image_activity" + activeActivityIndex}
              name={"image"}
              onChange={(e: any) =>
                handleFieldChange("image", e.target.files[0])
              }
              hidden
            />
            <div
              onClick={() =>
                document
                  .getElementById("image_activity" + activeActivityIndex)
                  ?.click()
              }
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
                minWidth: 100,
                minHeight: 100,
                width: 100,
                height: 100,
                borderRadius: 10,
                border: "1px solid lightgrey",
                padding: 5,
                overflow: "hidden",
              }}
            >
              {activeActivity?.image && (
                <img
                  style={{
                    borderRadius: 10,
                    minWidth: 90,
                    minHeight: 90,
                    objectFit: "cover",
                  }}
                  src={
                    activeActivity?.image
                      ? activeActivity?.image &&
                        (activeActivity.image as any) instanceof File
                        ? URL.createObjectURL(
                            activeActivity.image as unknown as File
                          )
                        : activeActivity?.image
                      : "/agenda.svg"
                  }
                />
              )}
              {!activeActivity?.image && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={70}
                  style={{ paddingLeft: 5 }}
                  height="70"
                  viewBox="0 0 18 18"
                  fill="none"
                >
                  <path
                    d="M17.6307 0.00157104C14.117 -0.0614313 10.1098 1.77539 7.57338 4.62475C5.14943 4.6705 2.78923 5.66429 1.05152 7.4021C0.950272 7.50185 0.914273 7.65111 0.958522 7.78612C1.00352 7.92187 1.12052 8.02012 1.26152 8.04037L4.15195 8.45439L3.79496 8.85416C3.66221 9.00266 3.66896 9.22842 3.80996 9.36943L8.6301 14.1899C8.70285 14.2626 8.79885 14.2994 8.8956 14.2994C8.98485 14.2994 9.07409 14.2679 9.14534 14.2041L9.54508 13.8471L9.95907 16.7377C9.97932 16.8787 10.0903 16.9822 10.2246 17.0272C10.2591 17.0385 10.2951 17.0437 10.3318 17.0437C10.4383 17.0437 10.5471 16.9972 10.6221 16.923C12.3358 15.2091 13.3295 12.8488 13.3752 10.4247C16.2274 7.88287 18.0791 3.87697 17.9974 0.367585C17.9921 0.168077 17.8309 0.00682124 17.6307 0.00157104ZM14.1747 6.47582C13.8095 6.84108 13.3295 7.02409 12.8488 7.02409C12.368 7.02409 11.888 6.84108 11.5228 6.47582C10.7923 5.74454 10.7923 4.55499 11.5228 3.82372C12.254 3.09244 13.4435 3.09244 14.1747 3.82372C14.906 4.55499 14.906 5.74529 14.1747 6.47582Z"
                    fill="#1777CF"
                  />
                  <path
                    d="M2.043 12.6785C1.24052 13.4811 0.13879 17.1067 0.0157927 17.517C-0.0239564 17.649 0.0127928 17.7922 0.109541 17.8897C0.181539 17.9618 0.276787 18 0.375035 18C0.411034 18 0.447033 17.9948 0.483032 17.9843C0.893273 17.8612 4.51869 16.7595 5.32118 15.9569C6.22491 15.0531 6.22491 13.5823 5.32118 12.6785C4.4167 11.7748 2.94673 11.7755 2.043 12.6785Z"
                    fill="#1777CF"
                  />
                </svg>
              )}
            </div>
            <div
              onClick={() =>
                document
                  .getElementById("image_activity" + activeActivityIndex)
                  ?.click()
              }
              style={{
                cursor: "pointer",
                boxShadow: "rgba(0, 0, 0, 0.2) -1px 2px 1px 0px",
                padding: 2,
                position: "absolute",
                top: 80,
                left: 70,
                background: "white",
                borderRadius: 10,
                overflow: "hidden",
              }}
            >
              <Camera width={18} height={18} />
            </div>
            <div style={{ padding: 10, paddingTop: 20 }}>
              <p style={{ fontSize: 14, color: "grey" }}>
                Atividade: {String(activeActivityIndex + 1).padStart(2, "0")}
              </p>
              <p style={{ fontSize: 16 }}>
                <b>{activeActivity?.name}</b>
              </p>
              {loadingFile && <CircularProgress size={18} />}
            </div>
          </div>

          {/* <div className="flex flex-col items-start justify-center gap-1.5 flex-1">
            <p className="w-full font-inter-18-regular text-clarotextos-03">
              Atividade: {String(activeActivityIndex + 1).padStart(2, "0")}
            </p>
            <h3 className="w-full font-inter-20-bold text-clarotextos-01">
              {activeActivity.name}
            </h3>
          </div> */}
        </div>
      </div>

      <Separator className="mb-5" />

      {showModalAddDocument == true && (
        <Dialog
          fullWidth
          maxWidth="lg"
          open={showModalAddDocument}
          onClose={() => {
            setSelectTypeModal("category");
            setSelectedCategoryDocument("");
            setShowModalAddDocument(false);
          }}
        >
          <DialogTitle>
            <h1 className="text-2xl font-bold mb-6">
              Adicionar e vincular documentos
            </h1>
            <IconButton
              aria-label="close"
              onClick={() => {
                setSelectTypeModal("category");
                setSelectedCategoryDocument("");
                setShowModalAddDocument(false);
              }}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {/* <DocumentForm onClose={async () => {
            setShowModalDocument(false);
            loadDocumentos();
          }} /> */}
            <div
              className="grid grid-cols-2"
              style={{
                padding: 10,
                backgroundColor: "#f2f2f2",
                margin: 2,
                height: "50vh",
              }}
            >
              <div
                className="scrollbar-hide"
                style={{
                  backgroundColor: "white",
                  borderRadius: 10,
                  position: "relative",
                  margin: 2,
                  height: "100%",
                  overflow: "scroll",
                }}
              >
                <div
                  style={{
                    background: "url(/pontilhado.png)",
                    position: "absolute",
                    zIndex: 1,
                    top: 20,
                    height: 110,
                    width: 100,
                    left: -82,
                    objectFit: "contain",
                    backgroundRepeat: "repeat-y",
                    backgroundSize: "262px 50px",
                  }}
                ></div>
                <div style={{ padding: 5 }}>
                  <p
                    style={{ display: "flex", alignItems: "center", zIndex: 2 }}
                  >
                    <svg
                      id="products-img1"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginRight: 5 }}
                      width={18}
                      height="18"
                      viewBox="0 0 15 20"
                    >
                      <path
                        d="M0.662897 3.35419C0.220942 3.81256 0 4.36119 0 4.99998L0 13.6666C0 14.3057 0.220942 14.8543 0.662897 15.3125C1.10489 15.771 1.63392 16 2.24999 16H2.89279V2.66658H2.24999C1.63378 2.66658 1.10478 2.89582 0.662897 3.35419Z"
                        fill="#7D8592"
                      />
                      <path
                        d="M12.8571 0.999959C12.8571 0.722158 12.7634 0.486097 12.5758 0.291629C12.3885 0.097161 12.1607 0 11.8929 0L6.10719 0C5.83938 0 5.61172 0.097161 5.42417 0.291629C5.23665 0.485987 5.14292 0.722122 5.14292 0.999959V2.66658H3.8572V16H14.1429V2.66658H12.8571V0.999959ZM11.5715 2.66658H6.42861V1.33329H11.5715V2.66658Z"
                        fill="#7D8592"
                      />
                      <path
                        d="M17.3371 3.35419C16.8951 2.89586 16.366 2.66658 15.75 2.66658H15.1071V16H15.75C16.3661 16 16.8951 15.771 17.3371 15.3125C17.779 14.8543 18 14.3056 18 13.6666V4.99998C18 4.36119 17.7789 3.81256 17.3371 3.35419Z"
                        fill="#7D8592"
                      />
                    </svg>{" "}
                    {formData.name}
                  </p>
                  <p
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginTop: 30,
                      zIndex: 2,
                    }}
                  >
                    <svg
                      id="fases-img1"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginRight: 5, zIndex: 2 }}
                      width={18}
                      height="18"
                      viewBox="0 0 15 20"
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M21.0725 0.60016V3.79598H9.26844V0.721711C9.26844 0.654605 9.32297 0.600098 9.39004 0.600098L21.0725 0.60016Z"
                        fill="#7D8592"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M18.8804 4.3012V7.49696H7.07638V4.42257C7.07638 4.35559 7.13091 4.30114 7.19798 4.30114L18.8804 4.3012Z"
                        fill="#7D8592"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M16.6883 8.00231V11.1979H4.88425V8.12374C4.88425 8.05682 4.93878 8.00231 5.00585 8.00231H16.6883Z"
                        fill="#7D8592"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M14.4962 11.7032V14.899H2.69213V11.8248C2.69213 11.7579 2.74659 11.7032 2.81373 11.7032L14.4962 11.7032Z"
                        fill="#7D8592"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M12.3041 15.4043V18.6001H0.5V15.5259C0.5 15.459 0.55453 15.4043 0.621601 15.4043H12.3041V15.4043Z"
                        fill="#7D8592"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M23.5 0.60016V18.6001H12.8096V15.4043H14.749C14.8886 15.4043 15.0017 15.2913 15.0017 15.1517V11.7033H16.9411C17.0807 11.7033 17.1938 11.5902 17.1938 11.4507V8.00231H19.1332C19.2729 8.00231 19.386 7.88912 19.386 7.74967V4.30127H21.3253C21.465 4.30127 21.5781 4.18807 21.5781 4.04862V0.60016H23.5Z"
                        fill="#7D8592"
                      />
                    </svg>{" "}
                    {activePhase.name}
                  </p>
                  <p
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginTop: 30,
                      zIndex: 2,
                    }}
                  >
                    <svg
                      id="atividades-img"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginRight: 5 }}
                      width={18}
                      height="18"
                      viewBox="0 0 15 20"
                    >
                      <path
                        d="M17.6307 0.00157104C14.117 -0.0614313 10.1098 1.77539 7.57338 4.62475C5.14943 4.6705 2.78923 5.66429 1.05152 7.4021C0.950272 7.50185 0.914273 7.65111 0.958522 7.78612C1.00352 7.92187 1.12052 8.02012 1.26152 8.04037L4.15195 8.45439L3.79496 8.85416C3.66221 9.00266 3.66896 9.22842 3.80996 9.36943L8.6301 14.1899C8.70285 14.2626 8.79885 14.2994 8.8956 14.2994C8.98485 14.2994 9.07409 14.2679 9.14534 14.2041L9.54508 13.8471L9.95907 16.7377C9.97932 16.8787 10.0903 16.9822 10.2246 17.0272C10.2591 17.0385 10.2951 17.0437 10.3318 17.0437C10.4383 17.0437 10.5471 16.9972 10.6221 16.923C12.3358 15.2091 13.3295 12.8488 13.3752 10.4247C16.2274 7.88287 18.0791 3.87697 17.9974 0.367585C17.9921 0.168077 17.8309 0.00682124 17.6307 0.00157104ZM14.1747 6.47582C13.8095 6.84108 13.3295 7.02409 12.8488 7.02409C12.368 7.02409 11.888 6.84108 11.5228 6.47582C10.7923 5.74454 10.7923 4.55499 11.5228 3.82372C12.254 3.09244 13.4435 3.09244 14.1747 3.82372C14.906 4.55499 14.906 5.74529 14.1747 6.47582Z"
                        fill="#7D8592"
                      />
                      <path
                        d="M2.043 12.6785C1.24052 13.4811 0.13879 17.1067 0.0157927 17.517C-0.0239564 17.649 0.0127928 17.7922 0.109541 17.8897C0.181539 17.9618 0.276787 18 0.375035 18C0.411034 18 0.447033 17.9948 0.483032 17.9843C0.893273 17.8612 4.51869 16.7595 5.32118 15.9569C6.22491 15.0531 6.22491 13.5823 5.32118 12.6785C4.4167 11.7748 2.94673 11.7755 2.043 12.6785Z"
                        fill="#7D8592"
                      />
                    </svg>{" "}
                    <b>Atividades ({activities.length})</b>
                  </p>
                </div>

                {activities.map((activity, index) => (
                  <div
                    key={index}
                    style={{
                      textOverflow: "ellipsis",
                      padding: 5,
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      setActiveActivityIndex(index);
                    }}
                  >
                    <p
                      style={{
                        color:
                          index == activeActivityIndex
                            ? "#1777CF"
                            : "rgba(0, 0, 0, 0.87)",
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>
                        {index + 1}. {activity.name}
                      </span>{" "}
                      <span>{Object.values(activity.documents).length}</span>
                    </p>
                    <Separator style={{ borderWidth: 0.5, marginTop: 5 }} />
                  </div>
                ))}
              </div>
              <div
                className="scrollbar-hide"
                style={{
                  backgroundColor: "white",
                  borderRadius: 10,
                  position: "relative",
                  padding: 10,
                  margin: 2,
                  height: "100%",
                  overflow: "scroll",
                }}
              >
                <div style={{ display: "flex" }}>
                  <div
                    onClick={(e) => {
                      setSelectTypeModal("category");
                    }}
                    style={{
                      width: "50%",
                      height: 40,
                      borderRadius: 5,
                      border: "1px solid #f2f2f2",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      textAlign: "center",
                      background:
                        selectTypeModal == "category" ? "#1777CF" : "white",
                      color:
                        selectTypeModal == "category" ? "white" : "#1777CF",
                    }}
                  >
                    Categorias
                  </div>
                  <div
                    onClick={(e) => {
                      if (!selectedCategoryDocument) {
                        return;
                      }
                      setSelectTypeModal("document");
                    }}
                    style={{
                      width: "50%",
                      height: 40,
                      borderRadius: 5,
                      border: "1px solid #f2f2f2",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      textAlign: "center",
                      background:
                        selectTypeModal == "document" ? "#1777CF" : "white",
                      color:
                        selectTypeModal == "document" ? "white" : "#1777CF",
                    }}
                  >
                    Documentos
                  </div>
                </div>

                {selectTypeModal == "category" && (
                  <div>
                    <p
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: 15,
                        zIndex: 2,
                        marginBottom: 2,
                      }}
                    >
                      <b>
                        Categorias (
                        {
                          categories.filter(
                            (item: any) => item.type == "document"
                          ).length
                        }
                        )
                      </b>{" "}
                      <div className="flex items-center gap-5">
                        <Button
                          size="icon"
                          className="w-[20px] h-[20px] bg-blue-500 rounded-md"
                          onClick={() => {
                            // setShowSelect(!showSelect)
                            setShowModalCategory(true);
                          }}
                        >
                          <PlusIcon className="h-4 w-4 text-white" />
                        </Button>
                      </div>
                    </p>
                    <p style={{ fontSize: 14, marginBottom: 10 }}>
                      Documentos:{" "}
                      {Object.values(activeActivity.documents).length}/
                      {documents.length}
                    </p>
                    {categories
                      .filter((item: any) => item.type == "document")
                      .map((activity, index) => (
                        <div
                          key={index}
                          style={{
                            textOverflow: "ellipsis",
                            padding: 5,
                            cursor: "pointer",
                            border: "1px solid #f2f2f2",
                            borderRadius: 5,
                          }}
                          onClick={(e) => {
                            setSelectedCategoryDocument(activity._id);
                          }}
                        >
                          <p
                            style={{
                              color:
                                activity._id == selectedCategoryDocument
                                  ? "#1777CF"
                                  : "rgba(0, 0, 0, 0.87)",
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>{activity.name}</span>{" "}
                            <span>
                              {
                                Object.values(activeActivity.documents).filter(
                                  (item: any) => item.category == activity._id
                                ).length
                              }
                              /
                              {
                                documents.filter(
                                  (item: any) => item.category == activity._id
                                ).length
                              }
                            </span>
                          </p>
                          {/* <Separator style={{ borderWidth: 0.5, marginTop: 5 }} /> */}
                        </div>
                      ))}
                  </div>
                )}

                {selectTypeModal == "document" && (
                  <div style={{ marginTop: 15 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        marginBottom: 10,
                      }}
                    >
                      <div
                        style={{
                          cursor: "pointer",
                          padding: 2,
                          borderRadius: 5,
                          border: "1px solid #f2f2f2",
                          fontSize: 15,
                          paddingLeft: 5,
                          paddingRight: 5,
                        }}
                        onClick={(e) => {
                          let index = categories
                            .filter((item: any) => item.type == "document")
                            .map((item: any, indexc: any) => {
                              if (item._id == selectedCategoryDocument) {
                                return indexc;
                              }
                              return -1;
                            })
                            .filter((item: any) => item != -1)[0];
                          if (index == 0) {
                            return;
                          }
                          setSelectedCategoryDocument(
                            categories.filter(
                              (item: any) => item.type == "document"
                            )[index - 1]._id
                          );
                        }}
                      >
                        <ChevronLeftIcon />
                      </div>
                      <div
                        style={{
                          padding: 2,
                          borderRadius: 5,
                          border: "1px solid #f2f2f2",
                          fontSize: 15,
                          paddingLeft: 5,
                          paddingRight: 5,
                        }}
                      >
                        {categories
                          .filter((item: any) => item.type == "document")
                          .map((item: any, indexc: any) => {
                            if (item._id == selectedCategoryDocument) {
                              return indexc;
                            }
                            return -1;
                          })
                          .filter((item) => item != -1)[0] + 1}
                        /
                        {
                          categories.filter(
                            (item: any) => item.type == "document"
                          ).length
                        }
                      </div>
                      <div
                        style={{
                          cursor: "pointer",
                          padding: 2,
                          borderRadius: 5,
                          border: "1px solid #f2f2f2",
                          fontSize: 15,
                          paddingLeft: 5,
                          paddingRight: 5,
                        }}
                        onClick={(e) => {
                          let index = categories
                            .filter((item: any) => item.type == "document")
                            .map((item: any, indexc: any) => {
                              if (item._id == selectedCategoryDocument) {
                                return indexc;
                              }
                              return -1;
                            })
                            .filter((item: any) => item != -1)[0];
                          if (
                            index ==
                            categories.filter(
                              (item: any) => item.type == "document"
                            ).length -
                              1
                          ) {
                            return;
                          }
                          setSelectedCategoryDocument(
                            categories.filter(
                              (item: any) => item.type == "document"
                            )[index + 1]._id
                          );
                        }}
                      >
                        <ChevronRightIcon />
                      </div>
                    </div>

                    <p
                      style={{
                        marginBottom: 10,
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>
                        {
                          categories
                            .filter((item: any) => item.type == "document")
                            .filter(
                              (item: any) =>
                                item._id == selectedCategoryDocument
                            )[0].name
                        }
                      </span>{" "}
                      <span>
                        {
                          Object.values(activeActivity.documents).filter(
                            (item: any) =>
                              item.category == selectedCategoryDocument
                          ).length
                        }
                        /
                        {
                          documents.filter(
                            (item: any) =>
                              item.category == selectedCategoryDocument
                          ).length
                        }
                      </span>{" "}
                    </p>
                    <Separator style={{ borderWidth: 0.5, marginTop: 5 }} />

                    <p
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: 15,
                        zIndex: 2,
                        marginBottom: 10,
                      }}
                    >
                      <b>
                        Documentos (
                        {
                          Object.values(activeActivity.documents).filter(
                            (item: any) =>
                              item.category == selectedCategoryDocument
                          ).length
                        }
                        /
                        {
                          documents.filter(
                            (item: any) =>
                              item.category == selectedCategoryDocument
                          ).length
                        }
                        )
                      </b>{" "}
                      <div className="flex items-center gap-5">
                        <Button
                          size="icon"
                          className="w-[20px] h-[20px] bg-blue-500 rounded-md"
                          onClick={() => {
                            // setShowSelect(!showSelect)
                            setShowModalDocument(true);
                          }}
                        >
                          <PlusIcon className="h-4 w-4 text-white" />
                        </Button>
                      </div>
                    </p>
                    {[
                      ...documents.filter(
                        (item: any) => item.category == selectedCategoryDocument
                      ),
                    ].map((activity: any, index) => (
                      <div
                        key={index}
                        style={{
                          textOverflow: "ellipsis",
                          padding: 5,
                          cursor: "pointer",
                          border: "1px solid #f2f2f2",
                          borderRadius: 5,
                        }}
                        onClick={(e) => {
                          if (activeActivity.documents[activity._id]) {
                            let documents_: any = {
                              ...activeActivity.documents,
                            };
                            delete documents_[activity._id];
                            const updatedActivity = {
                              ...activeActivity,
                              ["documents"]: documents_,
                            };

                            onActivityUpdate(
                              activePhaseIndex,
                              activeActivityIndex,
                              updatedActivity
                            );
                          } else {
                            handleAddDocument("documents", activity._id);
                          }

                          // setSelectedCategoryDocument(activity._id);
                        }}
                      >
                        <p
                          style={{
                            color: activeActivity.documents[activity._id]
                              ? "#1777CF"
                              : "rgba(0, 0, 0, 0.87)",
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <span>{activity.name}</span>{" "}
                          <span>
                            <AttachFile />
                          </span>
                        </p>
                        {/* <Separator style={{ borderWidth: 0.5, marginTop: 5 }} /> */}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
          {/* <DialogActions>
        {actions || (
          <Button onClick={onClose} color="primary">
            Fechar
          </Button>
        )}
      </DialogActions> */}
        </Dialog>
      )}

      {showModalAddTemplate == true && (
        <Dialog
          maxWidth="lg"
          fullWidth
          open={showModalAddTemplate}
          onClose={() => {
            setSelectTypeModal("category");
            setSelectedCategoryDocument("");
            setShowModalAddTemplate(false);
          }}
        >
          <DialogTitle>
            <h1 className="text-2xl font-bold mb-6">
              Adicionar e vincular templates
            </h1>
            <IconButton
              aria-label="close"
              onClick={() => {
                setSelectTypeModal("category");
                setSelectedCategoryDocument("");
                setShowModalAddTemplate(false);
              }}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {/* <DocumentForm onClose={async () => {
            setShowModalDocument(false);
            loadDocumentos();
          }} /> */}
            <div
              className="grid grid-cols-2"
              style={{
                padding: 10,
                backgroundColor: "#f2f2f2",
                margin: 2,
                height: "50vh",
              }}
            >
              <div
                className="scrollbar-hide"
                style={{
                  backgroundColor: "white",
                  borderRadius: 10,
                  position: "relative",
                  margin: 2,
                  height: "100%",
                  overflow: "scroll",
                }}
              >
                <div
                  style={{
                    background: "url(/pontilhado.png)",
                    position: "absolute",
                    zIndex: 1,
                    top: 20,
                    height: 110,
                    width: 100,
                    left: -82,
                    objectFit: "contain",
                    backgroundRepeat: "repeat-y",
                    backgroundSize: "262px 50px",
                  }}
                ></div>
                <div style={{ padding: 5 }}>
                  <p
                    style={{ display: "flex", alignItems: "center", zIndex: 2 }}
                  >
                    <svg
                      id="products-img1"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginRight: 5 }}
                      width={18}
                      height="18"
                      viewBox="0 0 15 20"
                    >
                      <path
                        d="M0.662897 3.35419C0.220942 3.81256 0 4.36119 0 4.99998L0 13.6666C0 14.3057 0.220942 14.8543 0.662897 15.3125C1.10489 15.771 1.63392 16 2.24999 16H2.89279V2.66658H2.24999C1.63378 2.66658 1.10478 2.89582 0.662897 3.35419Z"
                        fill="#7D8592"
                      />
                      <path
                        d="M12.8571 0.999959C12.8571 0.722158 12.7634 0.486097 12.5758 0.291629C12.3885 0.097161 12.1607 0 11.8929 0L6.10719 0C5.83938 0 5.61172 0.097161 5.42417 0.291629C5.23665 0.485987 5.14292 0.722122 5.14292 0.999959V2.66658H3.8572V16H14.1429V2.66658H12.8571V0.999959ZM11.5715 2.66658H6.42861V1.33329H11.5715V2.66658Z"
                        fill="#7D8592"
                      />
                      <path
                        d="M17.3371 3.35419C16.8951 2.89586 16.366 2.66658 15.75 2.66658H15.1071V16H15.75C16.3661 16 16.8951 15.771 17.3371 15.3125C17.779 14.8543 18 14.3056 18 13.6666V4.99998C18 4.36119 17.7789 3.81256 17.3371 3.35419Z"
                        fill="#7D8592"
                      />
                    </svg>{" "}
                    {formData.name}
                  </p>
                  <p
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginTop: 30,
                      zIndex: 2,
                    }}
                  >
                    <svg
                      id="fases-img1"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginRight: 5, zIndex: 2 }}
                      width={18}
                      height="18"
                      viewBox="0 0 15 20"
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M21.0725 0.60016V3.79598H9.26844V0.721711C9.26844 0.654605 9.32297 0.600098 9.39004 0.600098L21.0725 0.60016Z"
                        fill="#7D8592"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M18.8804 4.3012V7.49696H7.07638V4.42257C7.07638 4.35559 7.13091 4.30114 7.19798 4.30114L18.8804 4.3012Z"
                        fill="#7D8592"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M16.6883 8.00231V11.1979H4.88425V8.12374C4.88425 8.05682 4.93878 8.00231 5.00585 8.00231H16.6883Z"
                        fill="#7D8592"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M14.4962 11.7032V14.899H2.69213V11.8248C2.69213 11.7579 2.74659 11.7032 2.81373 11.7032L14.4962 11.7032Z"
                        fill="#7D8592"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M12.3041 15.4043V18.6001H0.5V15.5259C0.5 15.459 0.55453 15.4043 0.621601 15.4043H12.3041V15.4043Z"
                        fill="#7D8592"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M23.5 0.60016V18.6001H12.8096V15.4043H14.749C14.8886 15.4043 15.0017 15.2913 15.0017 15.1517V11.7033H16.9411C17.0807 11.7033 17.1938 11.5902 17.1938 11.4507V8.00231H19.1332C19.2729 8.00231 19.386 7.88912 19.386 7.74967V4.30127H21.3253C21.465 4.30127 21.5781 4.18807 21.5781 4.04862V0.60016H23.5Z"
                        fill="#7D8592"
                      />
                    </svg>{" "}
                    {activePhase.name}
                  </p>
                  <p
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginTop: 30,
                      zIndex: 2,
                    }}
                  >
                    <svg
                      id="atividades-img"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginRight: 5 }}
                      width={18}
                      height="18"
                      viewBox="0 0 15 20"
                    >
                      <path
                        d="M17.6307 0.00157104C14.117 -0.0614313 10.1098 1.77539 7.57338 4.62475C5.14943 4.6705 2.78923 5.66429 1.05152 7.4021C0.950272 7.50185 0.914273 7.65111 0.958522 7.78612C1.00352 7.92187 1.12052 8.02012 1.26152 8.04037L4.15195 8.45439L3.79496 8.85416C3.66221 9.00266 3.66896 9.22842 3.80996 9.36943L8.6301 14.1899C8.70285 14.2626 8.79885 14.2994 8.8956 14.2994C8.98485 14.2994 9.07409 14.2679 9.14534 14.2041L9.54508 13.8471L9.95907 16.7377C9.97932 16.8787 10.0903 16.9822 10.2246 17.0272C10.2591 17.0385 10.2951 17.0437 10.3318 17.0437C10.4383 17.0437 10.5471 16.9972 10.6221 16.923C12.3358 15.2091 13.3295 12.8488 13.3752 10.4247C16.2274 7.88287 18.0791 3.87697 17.9974 0.367585C17.9921 0.168077 17.8309 0.00682124 17.6307 0.00157104ZM14.1747 6.47582C13.8095 6.84108 13.3295 7.02409 12.8488 7.02409C12.368 7.02409 11.888 6.84108 11.5228 6.47582C10.7923 5.74454 10.7923 4.55499 11.5228 3.82372C12.254 3.09244 13.4435 3.09244 14.1747 3.82372C14.906 4.55499 14.906 5.74529 14.1747 6.47582Z"
                        fill="#7D8592"
                      />
                      <path
                        d="M2.043 12.6785C1.24052 13.4811 0.13879 17.1067 0.0157927 17.517C-0.0239564 17.649 0.0127928 17.7922 0.109541 17.8897C0.181539 17.9618 0.276787 18 0.375035 18C0.411034 18 0.447033 17.9948 0.483032 17.9843C0.893273 17.8612 4.51869 16.7595 5.32118 15.9569C6.22491 15.0531 6.22491 13.5823 5.32118 12.6785C4.4167 11.7748 2.94673 11.7755 2.043 12.6785Z"
                        fill="#7D8592"
                      />
                    </svg>{" "}
                    <b>Atividades ({activities.length})</b>
                  </p>
                </div>

                {activities.map((activity, index) => (
                  <div
                    key={index}
                    style={{
                      textOverflow: "ellipsis",
                      padding: 5,
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      setActiveActivityIndex(index);
                    }}
                  >
                    <p
                      style={{
                        color:
                          index == activeActivityIndex
                            ? "#1777CF"
                            : "rgba(0, 0, 0, 0.87)",
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>
                        {index + 1}. {activity.name}
                      </span>{" "}
                      <span>{Object.values(activity.templates).length}</span>
                    </p>
                    <Separator style={{ borderWidth: 0.5, marginTop: 5 }} />
                  </div>
                ))}
              </div>
              <div
                className="scrollbar-hide"
                style={{
                  backgroundColor: "white",
                  borderRadius: 10,
                  position: "relative",
                  padding: 10,
                  margin: 2,
                  height: "100%",
                  overflow: "scroll",
                }}
              >
                <div style={{ display: "flex" }}>
                  <div
                    onClick={(e) => {
                      setSelectTypeModal("category");
                    }}
                    style={{
                      width: "50%",
                      height: 40,
                      borderRadius: 5,
                      border: "1px solid #f2f2f2",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      textAlign: "center",
                      background:
                        selectTypeModal == "category" ? "#1777CF" : "white",
                      color:
                        selectTypeModal == "category" ? "white" : "#1777CF",
                    }}
                  >
                    Categorias
                  </div>
                  <div
                    onClick={(e) => {
                      if (!selectedCategoryDocument) {
                        return;
                      }
                      setSelectTypeModal("document");
                    }}
                    style={{
                      width: "50%",
                      height: 40,
                      borderRadius: 5,
                      border: "1px solid #f2f2f2",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      textAlign: "center",
                      background:
                        selectTypeModal == "document" ? "#1777CF" : "white",
                      color:
                        selectTypeModal == "document" ? "white" : "#1777CF",
                    }}
                  >
                    Templates
                  </div>
                </div>

                {selectTypeModal == "category" && (
                  <div>
                    <p
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: 15,
                        zIndex: 2,
                        marginBottom: 2,
                      }}
                    >
                      <b>
                        Categorias (
                        {
                          categories.filter(
                            (item: any) => item.type == "template"
                          ).length
                        }
                        )
                      </b>{" "}
                      <div className="flex items-center gap-5">
                        <Button
                          size="icon"
                          className="w-[20px] h-[20px] bg-blue-500 rounded-md"
                          onClick={() => {
                            // setShowSelect(!showSelect)
                            setShowModalCategory(true);
                          }}
                        >
                          <PlusIcon className="h-4 w-4 text-white" />
                        </Button>
                      </div>
                    </p>
                    <p style={{ fontSize: 14, marginBottom: 10 }}>
                      Templates:{" "}
                      {Object.values(activeActivity.templates).length}/
                      {templates.length}
                    </p>
                    {categories
                      .filter((item: any) => item.type == "template")
                      .map((activity, index) => (
                        <div
                          key={index}
                          style={{
                            textOverflow: "ellipsis",
                            padding: 5,
                            cursor: "pointer",
                            border: "1px solid #f2f2f2",
                            borderRadius: 5,
                          }}
                          onClick={(e) => {
                            setSelectedCategoryDocument(activity._id);
                          }}
                        >
                          <p
                            style={{
                              color:
                                activity._id == selectedCategoryDocument
                                  ? "#1777CF"
                                  : "rgba(0, 0, 0, 0.87)",
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>{activity.name}</span>{" "}
                            <span>
                              {
                                Object.values(activeActivity.templates).filter(
                                  (item: any) => item.category == activity._id
                                ).length
                              }
                              /
                              {
                                templates.filter(
                                  (item: any) => item.category == activity._id
                                ).length
                              }
                            </span>
                          </p>
                          {/* <Separator style={{ borderWidth: 0.5, marginTop: 5 }} /> */}
                        </div>
                      ))}
                  </div>
                )}

                {selectTypeModal == "document" && (
                  <div style={{ marginTop: 15 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        marginBottom: 10,
                      }}
                    >
                      <div
                        style={{
                          cursor: "pointer",
                          padding: 2,
                          borderRadius: 5,
                          border: "1px solid #f2f2f2",
                          fontSize: 15,
                          paddingLeft: 5,
                          paddingRight: 5,
                        }}
                        onClick={(e) => {
                          let index = categories
                            .filter((item: any) => item.type == "template")
                            .map((item: any, indexc: any) => {
                              if (item._id == selectedCategoryDocument) {
                                return indexc;
                              }
                              return -1;
                            })
                            .filter((item: any) => item != -1)[0];
                          if (index == 0) {
                            return;
                          }
                          setSelectedCategoryDocument(
                            categories.filter(
                              (item: any) => item.type == "template"
                            )[index - 1]._id
                          );
                        }}
                      >
                        <ChevronLeftIcon />
                      </div>
                      <div
                        style={{
                          padding: 2,
                          borderRadius: 5,
                          border: "1px solid #f2f2f2",
                          fontSize: 15,
                          paddingLeft: 5,
                          paddingRight: 5,
                        }}
                      >
                        {categories
                          .filter((item: any) => item.type == "template")
                          .map((item: any, indexc: any) => {
                            if (item._id == selectedCategoryDocument) {
                              return indexc;
                            }
                            return -1;
                          })
                          .filter((item: any) => item != -1)[0] + 1}
                        /
                        {
                          categories.filter(
                            (item: any) => item.type == "template"
                          ).length
                        }
                      </div>
                      <div
                        style={{
                          cursor: "pointer",
                          padding: 2,
                          borderRadius: 5,
                          border: "1px solid #f2f2f2",
                          fontSize: 15,
                          paddingLeft: 5,
                          paddingRight: 5,
                        }}
                        onClick={(e) => {
                          let index = categories
                            .filter((item: any) => item.type == "template")
                            .map((item: any, indexc: any) => {
                              if (item._id == selectedCategoryDocument) {
                                return indexc;
                              }
                              return -1;
                            })
                            .filter((item: any) => item != -1)[0];
                          if (
                            index ==
                            categories.filter(
                              (item: any) => item.type == "template"
                            ).length -
                              1
                          ) {
                            return;
                          }
                          setSelectedCategoryDocument(
                            categories.filter(
                              (item: any) => item.type == "template"
                            )[index + 1]._id
                          );
                        }}
                      >
                        <ChevronRightIcon />
                      </div>
                    </div>

                    <p
                      style={{
                        marginBottom: 10,
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>
                        {
                          categories
                            .filter((item: any) => item.type == "template")
                            .filter(
                              (item: any) =>
                                item._id == selectedCategoryDocument
                            )[0]?.name
                        }
                      </span>{" "}
                      <span>
                        {
                          Object.values(activeActivity.templates).filter(
                            (item: any) =>
                              item.category == selectedCategoryDocument
                          ).length
                        }
                        /
                        {
                          templates.filter(
                            (item: any) =>
                              item.category == selectedCategoryDocument
                          ).length
                        }
                      </span>{" "}
                    </p>
                    <Separator style={{ borderWidth: 0.5, marginTop: 5 }} />

                    <p
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: 15,
                        zIndex: 2,
                        marginBottom: 10,
                      }}
                    >
                      <b>
                        Templates (
                        {
                          Object.values(activeActivity.templates).filter(
                            (item: any) =>
                              item.category == selectedCategoryDocument
                          ).length
                        }
                        /
                        {
                          templates.filter(
                            (item: any) =>
                              item.category == selectedCategoryDocument
                          ).length
                        }
                        )
                      </b>{" "}
                      <div className="flex items-center gap-5">
                        <Button
                          size="icon"
                          className="w-[20px] h-[20px] bg-blue-500 rounded-md"
                          onClick={() => {
                            // setShowSelect(!showSelect)
                            setShowModalTemplate(true);
                          }}
                        >
                          <PlusIcon className="h-4 w-4 text-white" />
                        </Button>
                      </div>
                    </p>
                    {[
                      ...templates.filter(
                        (item: any) => item.category == selectedCategoryDocument
                      ),
                    ].map((activity: any, index) => (
                      <div
                        key={index}
                        style={{
                          textOverflow: "ellipsis",
                          padding: 5,
                          cursor: "pointer",
                          border: "1px solid #f2f2f2",
                          borderRadius: 5,
                        }}
                        onClick={(e) => {
                          if (activeActivity.templates[activity._id]) {
                            let documents_: any = {
                              ...activeActivity.templates,
                            };
                            delete documents_[activity._id];
                            const updatedActivity = {
                              ...activeActivity,
                              ["templates"]: documents_,
                            };

                            onActivityUpdate(
                              activePhaseIndex,
                              activeActivityIndex,
                              updatedActivity
                            );
                          } else {
                            console.log("template selecionado", activity);
                            handleAddTemplate("templates", activity._id);
                          }

                          // setSelectedCategoryDocument(activity._id);
                        }}
                      >
                        <p
                          style={{
                            color: activeActivity.templates[activity._id]
                              ? "#1777CF"
                              : "rgba(0, 0, 0, 0.87)",
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <span>{activity.name}</span>{" "}
                          <span>
                            <AttachFile />
                          </span>
                        </p>
                        {/* <Separator style={{ borderWidth: 0.5, marginTop: 5 }} /> */}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
          {/* <DialogActions>
        {actions || (
          <Button onClick={onClose} color="primary">
            Fechar
          </Button>
        )}
      </DialogActions> */}
        </Dialog>
      )}

      {/* Activity Form */}
      <div className="w-full flex flex-col gap-[15px] mb-5">
        {/* Activity Number */}
        <div className="flex w-full items-start gap-[15px]">
          <div className="flex flex-col items-start justify-center gap-1.5 w-full">
            <label className="text-sm font-semibold text-gray-700">
              N° da Atividade <b style={{ color: "red" }}>*</b>
            </label>
            <Input
              className="h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              value={String(activeActivityIndex + 1).padStart(2, "0")}
              required
              disabled
            />
          </div>
        </div>

        {/* Title */}
        <div className="flex flex-col w-full items-start justify-center gap-1.5">
          <label className="text-sm font-semibold text-gray-700">
            Título <b style={{ color: "red" }}>*</b>
          </label>
          <Input
            className={`h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
              error?.validationErrors?.[
                `phases.${activePhaseIndex}.activities.${activeActivityIndex}.name`
              ]
                ? "border-red-500"
                : ""
            }`}
            placeholder="Digite o nome da atividade"
            required
            value={activeActivity.name}
            onChange={(e) => handleFieldChange("name", e.target.value)}
          />
          {error?.validationErrors?.[
            `phases.${activePhaseIndex}.activities.${activeActivityIndex}.name`
          ] && (
            <p className="text-red-500 text-sm px-1.5">
              {
                error.validationErrors[
                  `phases.${activePhaseIndex}.activities.${activeActivityIndex}.name`
                ].message
              }
            </p>
          )}
        </div>

        {/* Description */}
        <div className="flex flex-col w-full items-start justify-center gap-1.5">
          <label className="text-sm font-semibold text-gray-700">
            Descrição
          </label>

          <Textarea
            className={`min-h-[120px] rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
              error?.validationErrors?.[
                `phases.${activePhaseIndex}.activities.${activeActivityIndex}.description`
              ]
                ? "border-red-500"
                : ""
            }`}
            placeholder="Digite aqui..."
            value={activeActivity.description || ""}
            onChange={(e) => handleFieldChange("description", e.target.value)}
          />
          {error?.validationErrors?.[
            `phases.${activePhaseIndex}.activities.${activeActivityIndex}.description`
          ] && (
            <p className="text-red-500 text-sm px-1.5">
              {
                error.validationErrors[
                  `phases.${activePhaseIndex}.activities.${activeActivityIndex}.description`
                ].message
              }
            </p>
          )}
        </div>

        {/* Duration */}
        <div className="flex w-full items-start gap-[15px]">
          {/* Days */}
          <div className="flex flex-col items-start justify-center gap-1.5 flex-1">
            <div className="flex items-center gap-1.5 px-1.5 w-full">
              <CalendarIcon className="h-4 w-4" />
              <label className="text-sm font-semibold text-gray-700">
                Dias <b style={{ color: "red" }}>*</b>
              </label>
              {/* <label className="font-inter-14-bold text-clarotextos-02">
                Dias
              </label> */}
            </div>
            <Input
              className="h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              required
              value={activeActivity.days}
              onChange={(e) => {
                handleFieldChange("days", e.target.value);
              }}
              type="number"
              min="0"
            />
          </div>

          {/* Hours */}
          <div className="flex flex-col items-start justify-center gap-1.5 flex-1">
            <div className="flex items-center gap-1.5 px-1.5 w-full">
              <ClockIcon className="h-4 w-4" />
              {/* <label className="font-inter-14-bold text-clarotextos-02">
                Horas
              </label> */}
              <label className="text-sm font-semibold text-gray-700">
                Horas <b style={{ color: "red" }}>*</b>
              </label>
            </div>
            <Input
              className="h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              value={activeActivity.hours}
              required
              onChange={(e) => {
                handleFieldChange("hours", e.target.value);
              }}
              type="number"
              min="0"
              max="23"
            />
          </div>
        </div>

        {/* Activity Type */}
        {/* <div className="flex flex-col w-full items-start justify-center gap-1.5">
              <label className="text-sm font-semibold text-gray-700">
            Tipo de Atividade
          </label>
          <div className="flex items-center gap-5 w-full">
            {[
              { type: ActivityType.FORM, label: 'Formulário' },
              { type: ActivityType.EXTERNAL, label: 'Link Externo' },
              { type: ActivityType.DOCUMENT_GENERATION, label: 'Geração de Documento' }
            ].map(({ type, label }) => (
              <Button
                key={type}
                variant="outline"
                className={`flex items-center justify-center gap-2.5 p-2.5 flex-1 rounded-xl ${
                  activeActivity.type === type
                    ? "border-[#1776cf] border text-claroprimaria-01"
                    : "border-[#d8e0f0] border-[0.5px] text-clarotextos-02"
                } bg-clarofrente-01`}
                onClick={() => handleFieldChange("type", type)}
              >
                <span className="font-inter-16-regular whitespace-nowrap">
                  {label}
                </span>
              </Button>
            ))}
          </div>
        </div> */}

        {/* {activeActivity.type === ActivityType.FORM && (
          <div className="flex flex-col w-full items-start justify-center gap-1.5">
              <label className="text-sm font-semibold text-gray-700">
              Formulário
            </label>
            <select
            className={`w-full h-12 border rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200`}
              value={activeActivity.typeRefId || ""}
              onChange={(e) => handleFieldChange("typeRefId", e.target.value)}
            >
              <option value="">Selecione um formulário</option>
              {availableForms.map(form => (
                <option key={form._id} value={form._id}>
                  {form.name}
                </option>
              ))}
            </select>
          </div>
        )}  */}
      </div>

      {/* <Separator className="mb-1" /> */}

      {/* Tutorial Section */}
      <TutorialSection
        tutorial={activeActivity.tutorial}
        onChange={(tutorial) => handleFieldChange("tutorial", tutorial)}
        onUpload={saveFile}
        loadingFile={loadingFileTutorial}
        id={activeActivity._id || `activity-${activeActivityIndex}`}
      />

      <div className="flex flex-col items-start gap-1.5 w-full">
        <div className="flex w-full h-9 items-center justify-between mb-5">
          <h1 className="text-xl font-bold" id="atividades">
            Documentos ({Object.values(activeActivity.documents).length})
          </h1>

          <div className="flex items-center gap-5">
            <Button
              size="icon"
              className="w-[30px] h-[30px] bg-blue-500 rounded-lg"
              onClick={() => {
                // setShowSelect(!showSelect)
                setShowModalAddDocument(true);
              }}
            >
              <PlusIcon className="h-4 w-4 text-white" />
            </Button>
          </div>
        </div>
        {showSelect && (
          <select
            title="Selecione uma categoria"
            className={`w-full h-12 border rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200`}
            value={selectedCategoryDocument}
            onChange={(e) => {
              if (e.target.value == "new_document") {
                setShowModalCategory(true);
              } else {
                setSelectedCategoryDocument(e.target.value);
              }
            }}
          >
            <option value="">Selecione uma categoria</option>
            <option value="new_document">Adicionar nova categoria</option>

            {categories
              .filter((item: any) => {
                return item.type == "document";
              })
              .map((doc: any) => (
                <option key={doc._id} value={doc._id}>
                  {doc.name}
                </option>
              ))}
          </select>
        )}
        {showSelect && (
          <select
            title="Selecione um ou mais documentos"
            className={`w-full h-12 border rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200`}
            value={""}
            onChange={(e) => {
              handleAddDocument("documents", e.target.value);
            }}
          >
            <option value="">Selecione um ou mais documentos</option>
            <option value="new_document">Adicionar novo documento</option>

            {documents
              .filter((item: any) => {
                return item.category == selectedCategoryDocument;
              })
              .map((doc: any) => (
                <option key={doc._id} value={doc._id}>
                  {doc.name}
                </option>
              ))}
          </select>
        )}
      </div>
      <div className="w-full">
        <DataTable
          hide_search
          hide_add
          hideEdit
          columns={[
            {
              image: "photoUrl",
              placeholderImage: "/documents.svg",
              style_image: {
                borderRadius: 10,
                border: "1px solid lightgrey",
                background: "rgb(244, 244, 244)",
                width: 30,
                height: 30,
                objectFit: "contain",
              },

              key: "name",
              header: "Nome",
              render: (value: any) => String(value || ""),
            },
            {
              key: "category",
              header: "Categoria",
              render: (value: any) =>
                String(
                  categories.filter((item: any) => item._id == value)[0]
                    ?.name || ""
                ),
            },
          ]}
          hide_header
          data={Object.values(activeActivity.documents) ?? []}
          basePath="documents"
          onDelete={(e) => {
            let documents_: any = { ...activeActivity.documents };
            delete documents_[e];
            const updatedActivity = {
              ...activeActivity,
              ["documents"]: documents_,
            };

            onActivityUpdate(
              activePhaseIndex,
              activeActivityIndex,
              updatedActivity
            );
          }}
          // error={error}
          newFormat
        />
      </div>

      <div className="flex flex-col items-start gap-1.5 w-full">
        <div className="flex w-full h-9 items-center justify-between mb-5">
          <h1 className="text-xl font-bold" id="atividades">
            Templates ({Object.values(activeActivity.templates).length})
          </h1>

          <div className="flex items-center gap-5">
            <Button
              size="icon"
              className="w-[30px] h-[30px] bg-blue-500 rounded-lg"
              onClick={() => {
                // setShowSelectTemplate(!showSelectTemplate)
                setShowModalAddTemplate(true);
              }}
            >
              <PlusIcon className="h-4 w-4 text-white" />
            </Button>
          </div>
        </div>
        {showSelectTemplate && (
          <select
            title="Selecione uma categoria de template"
            className={`w-full h-12 border rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200`}
            value={selectedCategoryTemplate}
            onChange={(e) => {
              if (e.target.value == "new_document") {
                setShowModalCategory(true);
              } else {
                setSelectedCategoryTemplate(e.target.value);
              }
            }}
          >
            <option value="">Selecione uma categoria</option>
            <option value="new_document">Adicionar nova categoria</option>

            {categories
              .filter((item: any) => {
                return item.type == "template";
              })
              .map((doc: any) => (
                <option key={doc._id} value={doc._id}>
                  {doc.name}
                </option>
              ))}
          </select>
        )}
        {showSelectTemplate && (
          <select
            title="Selecione um ou mais templates"
            className={`w-full h-12 border rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200`}
            value={""}
            onChange={(e) => {
              handleAddTemplate("templates", e.target.value);
            }}
          >
            <option value="">Selecione um ou mais templates</option>
            <option value="new_document">Adicionar novo template</option>

            {templates
              .filter((item: any) => {
                return item.category == selectedCategoryTemplate;
              })
              .map((doc: any) => (
                <option key={doc._id} value={doc._id}>
                  {doc.name}
                </option>
              ))}
          </select>
        )}
      </div>
      <div className="w-full">
        <DataTable
          hide_search
          hide_add
          hideEdit
          columns={[
            {
              image: "photoUrl",
              placeholderImage: "/documents.svg",
              style_image: {
                borderRadius: 10,
                border: "1px solid lightgrey",
                background: "rgb(244, 244, 244)",
                width: 30,
                height: 30,
                objectFit: "contain",
              },

              key: "name",
              header: "Nome",
              render: (value: any) => String(value || ""),
            },
          ]}
          hide_header
          data={Object.values(activeActivity.templates) ?? []}
          basePath="templates"
          onDelete={(e) => {
            let documents_: any = { ...activeActivity.templates };
            delete documents_[e];
            const updatedActivity = {
              ...activeActivity,
              ["templates"]: documents_,
            };

            onActivityUpdate(
              activePhaseIndex,
              activeActivityIndex,
              updatedActivity
            );
          }}
          // error={error}
          newFormat
        />
      </div>
      {
        <ApplicationSection
          forms={availableForms}
          activity={activeActivity}
          handleFieldChange={handleFieldChange}
          setSelectedTab={setTabSelect}
          selectedTab={tabSelect}
          setAppointments={setAppointments}
          templates={templates}
          techinicals={techinicals}
          categories={categories}
          documents={documents}
          saveFile={saveFile}
        />
      }

      <Dialog
        open={showModalDocument}
        onClose={() => {
          setShowModalDocument(false);
        }}
      >
        <DialogTitle>
          <h1 className="text-xl font-bold mb-6">Cadastrar novo documento</h1>
          <IconButton
            aria-label="close"
            onClick={() => {
              setShowModalDocument(false);
            }}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DocumentForm
            onClose={async () => {
              setShowModalDocument(false);
              loadDocumentos();
            }}
          />
        </DialogContent>
        {/* <DialogActions>
        {actions || (
          <Button onClick={onClose} color="primary">
            Fechar
          </Button>
        )}
      </DialogActions> */}
      </Dialog>

      <Dialog
        open={showModalTemplate}
        onClose={() => {
          setShowModalTemplate(false);
        }}
      >
        <DialogTitle>
          <h1 className="text-xl font-bold mb-6">Cadastrar novo template</h1>
          <IconButton
            aria-label="close"
            onClick={() => {
              setShowModalTemplate(false);
            }}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TemplateForm
            onClose={async () => {
              setShowModalTemplate(false);
              loadDocumentos();
            }}
          />
        </DialogContent>
        {/* <DialogActions>
        {actions || (
          <Button onClick={onClose} color="primary">
            Fechar
          </Button>
        )}
      </DialogActions> */}
      </Dialog>

      <Dialog
        open={showModalCategory}
        onClose={() => {
          setShowModalCategory(false);
        }}
      >
        <DialogTitle>
          <h1 className="text-xl font-bold mb-6">Cadastrar nova categoria</h1>
          <IconButton
            aria-label="close"
            onClick={() => {
              setShowModalCategory(false);
            }}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <CategoryDocumentForm
            onClose={async () => {
              setShowModalCategory(false);
              loadDocumentos();
            }}
          />
        </DialogContent>
        {/* <DialogActions>
        {actions || (
          <Button onClick={onClose} color="primary">
            Fechar
          </Button>
        )}
      </DialogActions> */}
      </Dialog>
    </section>
  );
};
